import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { toast } from "react-toastify";

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("orderId");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Clean up checkout data from localStorage once order is confirmed
    localStorage.removeItem("checkoutData");

    // Optional: Google Ads / Analytics conversion tracking
    const script1 = document.createElement("script");
    script1.src = "https://www.googletagmanager.com/gtag/js?id=AW-16647750621";
    script1.async = true;
    document.head.appendChild(script1);

    script1.onload = () => {
      const script2 = document.createElement("script");
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-16647750621');
      `;
      document.head.appendChild(script2);
    };

    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-16647750621/zBwdCKHYjcEaEN2HooI-",
        transaction_id: orderId || "",
        currency: "INR",
      });
    }

    return () => {
      const scripts = document.querySelectorAll(
        'script[src="https://www.googletagmanager.com/gtag/js?id=AW-16647750621"]'
      );
      scripts.forEach((script) => script.remove());
    };
  }, [orderId]);

  const handleCopyOrderId = () => {
    if (orderId) {
      navigator.clipboard.writeText(orderId);
      setCopied(true);
      toast.success("Order ID copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-orange-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center relative overflow-hidden">
        {/* Top green accent bar */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-green-500 to-emerald-500" />

        {/* Success Icon */}
        <div className="mx-auto flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-5">
          <CheckCircleIcon className="w-14 h-14 text-green-600 animate-pulse" />
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
          Order Placed Successfully!
        </h1>
        <p className="mt-2 text-gray-600 text-sm">
          Thank you for shopping with BharatCod. Your payment was successful and your order has been confirmed.
        </p>

        {/* Order ID Box */}
        {orderId && (
          <div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-left">
              <span className="text-xs uppercase font-semibold text-gray-400 block">
                Order ID
              </span>
              <span className="text-lg font-bold text-gray-800 font-mono">
                {orderId}
              </span>
            </div>
            <button
              onClick={handleCopyOrderId}
              type="button"
              className="text-xs bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 font-medium transition shadow-sm"
            >
              {copied ? "✓ Copied" : "Copy ID"}
            </button>
          </div>
        )}

        {/* Information box */}
        <div className="mt-6 text-left bg-orange-50/70 border border-orange-100 rounded-xl p-4 text-xs text-gray-700 space-y-2">
          <p className="flex items-center gap-2">
            <span className="text-green-600 font-bold">✔</span>
            <span>Payment successfully verified via PayU.</span>
          </p>
          <p className="flex items-center gap-2">
            <span className="text-green-600 font-bold">✔</span>
            <span>We are packing your items and will notify you when shipped.</span>
          </p>
          <p className="flex items-center gap-2">
            <span className="text-green-600 font-bold">✔</span>
            <span>You will receive an SMS and email notification with tracking updates.</span>
          </p>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate("/track-your-order/")}
            className="flex-1 py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl shadow-md transition duration-200"
          >
            Track Your Order
          </button>
          <Link
            to="/"
            className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-xl text-center transition duration-200"
          >
            Continue Shopping
          </Link>
        </div>

        {/* App download section */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            For fastest delivery updates and easy tracking,{" "}
            <a
              href="https://play.google.com/store/apps/details?id=com.bharatcod"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 font-semibold hover:underline"
            >
              Download BharatCod App
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
